/**
 * Briscola — server-authoritative rules engine (Italian trick-taking game, 4 players in two partnerships).
 *
 * Rules implemented (toggles live in {@link BriscolaConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1).
 *  - 40-card Italian-style deck: the standard deck without the 8, 9 and 10. Ranking, high to low: A, 3, K, Q, J, 7, 6,
 *    5, 4, 2. Card points: A 11, 3 10, K 4, Q 3, J 2, all others 0 — 30 per suit, 120 in the pack.
 *  - Each player is dealt three cards; the next card is turned face up and the suit is trump (the briscola). The face-up
 *    card stays at the bottom of the stock and is drawn last.
 *  - The player left of the dealer leads. There is no obligation to follow suit. The trick goes to the highest trump,
 *    else to the highest card of the led suit. The winner collects the cards, draws first from the stock and leads next;
 *    the others draw in turn order. When the stock runs out the last cards are played out (10 tricks in all).
 *  - A partnership wins the hand with more than 60 of the 120 points; 60–60 is a draw and the hand is dealt again.
 *  - The match ends when a partnership has won `targetScore` hands. `maxHands` is a hard cap so a match always
 *    terminates (then the team with more hands wins, equal totals share first). Partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface BriscolaConfig {
  /** Match ends when a partnership has won this many hands. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type BriscolaPhase = 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface BriscolaTrickPlay {
  seat: number;
  card: CardId;
}

export interface BriscolaCompletedTrick {
  leader: number;
  plays: BriscolaTrickPlay[];
  winner: number;
  /** Card points in the trick. */
  points: number;
}

/** Per-hand result for one partnership (public once the hand ends). */
export interface BriscolaTeamHand {
  /** Card points collected (out of 120). */
  points: number;
  /** True when the partnership took more than 60 points. */
  won: boolean;
}

export interface BriscolaState {
  config: BriscolaConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: BriscolaPhase;
  hands: CardId[][];
  /** Undealt cards; index 0 is drawn next and the last card is {@link trumpCard}. */
  stock: CardId[];
  /** The face-up card at the bottom of the stock; its suit is trump. */
  trumpCard: CardId;
  trump: Suit;
  /** Card points collected by each team this hand. */
  teamPoints: number[];
  /** Tricks won by each seat this hand. */
  tricksWon: number[];
  turn: number;
  trick: BriscolaTrickPlay[];
  tricks: BriscolaCompletedTrick[];
  /** Hands won by each team, index = team (seat % 2). */
  teamScores: number[];
  /** One row per finished hand: [team0, team1]. */
  handResults: BriscolaTeamHand[][];
}

export type BriscolaAction = { type: 'play'; card: CardId };

export type BriscolaLegal = { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface BriscolaView {
  gameType: 'BRISCOLA';
  config: BriscolaConfig;
  seat: number | null;
  phase: BriscolaPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  /** Cards left in the stock, including the face-up trump card while it is still there. */
  stockCount: number;
  trumpCard: CardId;
  trump: Suit;
  /** True while the face-up card is still at the bottom of the stock. */
  trumpCardInStock: boolean;
  teamPoints: number[];
  tricksWon: number[];
  trick: BriscolaTrickPlay[];
  lastTrick: BriscolaCompletedTrick | null;
  tricks: BriscolaCompletedTrick[];
  /** Hands won by each team. */
  teamScores: number[];
  handResults: BriscolaTeamHand[][];
  /** Hands won by each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: BriscolaConfig = {
  targetScore: 1,
  maxHands: 9,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const TRICKS = 10;
const HAND_POINTS = 120;

export const briscolaTeamOf = (seat: number): number => seat % 2;
export const briscolaPartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** Card points: A 11, 3 10, K 4, Q 3, J 2. */
export function briscolaPoints(card: CardId): number {
  switch (parseCard(card).rank) {
    case 14:
      return 11;
    case 3:
      return 10;
    case 13:
      return 4;
    case 12:
      return 3;
    case 11:
      return 2;
    default:
      return 0;
  }
}

/** Trick-taking strength within a suit: A > 3 > K > Q > J > 7 > 6 > 5 > 4 > 2. */
export function briscolaStrength(card: CardId): number {
  switch (parseCard(card).rank) {
    case 14:
      return 10;
    case 3:
      return 9;
    case 13:
      return 8;
    case 12:
      return 7;
    case 11:
      return 6;
    case 2:
      return 1;
    default:
      // 7 → 5, 6 → 4, 5 → 3, 4 → 2
      return parseCard(card).rank - 2;
  }
}

/** The 40-card Briscola deck. */
export const briscolaDeck = (): CardId[] => buildDeck().filter((c) => { const r = parseCard(c).rank; return r !== 8 && r !== 9 && r !== 10; });

export function briscolaTrickWinner(plays: readonly BriscolaTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = c.suit === b.suit ? briscolaStrength(p.card) > briscolaStrength(best.card) : c.suit === trump;
    if (beats && (c.suit === trump || c.suit === lead)) best = p;
  }
  return best.seat;
}

/** Any card in hand may be played: there is no obligation to follow suit. */
export const briscolaLegalCards = (hand: readonly CardId[]): CardId[] => [...hand];

function deal(state: BriscolaState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(briscolaDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < 3 * SEATS; i++) hands[(state.dealer + 1 + i) % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.stock = deck.slice(3 * SEATS);
  state.trumpCard = state.stock[state.stock.length - 1]!;
  state.trump = parseCard(state.trumpCard).suit;
  state.teamPoints = [0, 0];
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, trumpCard: state.trumpCard, trump: state.trump, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the team with more hands won is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: BriscolaState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = briscolaTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

export const briscolaEngine: GameEngine<BriscolaConfig, BriscolaState, BriscolaAction, BriscolaView, BriscolaLegal> = {
  gameType: 'BRISCOLA',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 10);
    checkInt('maxHands', c.maxHands, 1, 30);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Briscola needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: BriscolaState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      hands: [],
      stock: [],
      trumpCard: '2S',
      trump: 'S',
      teamPoints: [0, 0],
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Briscola action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && state.phase === 'PLAYING', 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = briscolaTrickWinner(state.trick, state.trump);
    const points = state.trick.reduce((n, p) => n + briscolaPoints(p.card), 0);
    state.tricksWon[winner]!++;
    state.teamPoints[briscolaTeamOf(winner)]! += points;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, points });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, points });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn || state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: briscolaLegalCards(state.hands[seat]!) };
  },

  getTimeoutAction(state, seat) {
    // The cheapest card: no points first, trump last, lowest strength first.
    const cost = (c: CardId) => (parseCard(c).suit === state.trump ? 1000 : 0) + briscolaPoints(c) * 20 + briscolaStrength(c);
    const lowest = [...state.hands[seat]!].sort((a, b) => cost(a) - cost(b))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'BRISCOLA',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      stockCount: state.stock.length,
      trumpCard: state.trumpCard,
      trump: state.trump,
      trumpCardInStock: state.stock.includes(state.trumpCard),
      teamPoints: [...state.teamPoints],
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[briscolaTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      // The trick winner draws first, then the others in turn order; the last card drawn is the face-up trump card.
      for (let i = 0; i < SEATS && state.stock.length > 0; i++) {
        state.hands[(state.turn + i) % SEATS]!.push(state.stock.shift()!);
      }
      state.hands = state.hands.map((h) => sortHand(h));
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn, stock: state.stock.length });
        return events;
      }
      const cfg = state.config;
      const results: BriscolaTeamHand[] = [0, 1].map((team) => ({ points: state.teamPoints[team]!, won: state.teamPoints[team]! > HAND_POINTS / 2 }));
      results.forEach((r, team) => {
        if (r.won) state.teamScores[team]!++;
      });
      state.handResults.push(results);
      events.push({ type: 'handEnd', hand: state.hand, results, teamScores: [...state.teamScores], teamPoints: [...state.teamPoints] });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      const capped = state.hand >= cfg.maxHands;
      if (reached || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
